/* eslint-disable */
import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import notification from "../../components/notification";
import { serializeQueryParams } from "../../utils";

const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

// Define a service using a base URL and expected endpoints
export const swasnaAdminApi = createApi({
  reducerPath: "swasnaAdminApi",
  baseQuery: fetchBaseQuery({
    //baseUrl: process.env[`REACT_APP_API_URL_${process.env.REACT_APP_ENVIRONMENT.toUpperCase()}`],
    // baseUrl: `http://localhost:3000/v1`,
   
    baseUrl: `${process.env.REACT_APP_BASENAME || "https://api-swasna.thesynergyworks.com/v1"}`,
    prepareHeaders: (headers) => {
      headers.set("Authorization", `Bearer ${localStorage.getItem("token")}`);
      return headers;
    },
  }),
  tagTypes: [
    "services",
    "packages",
    "availability",
    "expertise",
    "providers",
    "expertise",
    "memberships",
    "appointments",
    "leads",
    "profile",
    "settings",
    "remainders",
    "analytics",
    "family",
    "transactions",
  ],
  endpoints: (builder) => ({
    login: builder.mutation({
      query: ({ email, password, rememberMe = false }) => ({
        url: "/login",
        method: "POST",
        body: {
          email,
          password,
          uuid: localStorage.getItem("uuid"),
          role: "admin",
          rememberMe,
        },
      }),
      onQueryStarted(body, { queryFulfilled }) {
        queryFulfilled.catch((err) =>
          notification({
            title: "Invalid credentials!",
            description:
              err?.data?.message ||
              err?.error?.data?.message ||
              "Either your email or password is invalid",
            type: "error",
          }),
        );
      },
    }),
    verify: builder.mutation({
      query: ({ verifyToken, otp }) => ({
        url: "/verify",
        method: "POST",
        body: { verifyToken, otp },
      }),
      onQueryStarted(body, { queryFulfilled }) {
        queryFulfilled.catch((error) => {
          if (error.data.code === "BAD_INPUT_ERROR") {
            notification({
              title: "Invalid OTP!",
              description: "Please enter correct otp",
            });
          } else {
            notification({
              title: "Login required!",
              description: "Please login first and then try again",
            });
          }
        });
      },
    }),
    forgotPassword: builder.mutation({
      query: ({ email }) => ({
        url: "/forgot-password",
        method: "PUT",
        body: { email, role: "admin" },
      }),
      onQueryStarted(body, { queryFulfilled }) {
        queryFulfilled
          .then(() =>
            notification({
              title: "Email sent successfully!",
              description:
                "Check your email for directions to reset your password",
              type: "success",
            }),
          )
          .catch(() =>
            notification({
              title: "Invalid Email!",
              description: "No user found with that email",
              type: "error",
            }),
          );
      },
    }),
    resetPassword: builder.mutation({
      query: ({ forgotPasswordToken, newPassword }) => ({
        url: "/reset-password",
        method: "PATCH",
        body: { forgotPasswordToken, newPassword },
      }),
      onQueryStarted(body, { queryFulfilled }) {
        queryFulfilled.then(() =>
          notification({
            title: "Password Updated!",
            description: "Please login to continue",
            type: "success",
          }),
        );
      },
    }),
    getServices: builder.query({
      query: ({ ...queryParams }) => ({
        url: `/services?${serializeQueryParams(queryParams)}`,
        method: "GET",
      }),
      providesTags: ["services"],
    }),
    createService: builder.mutation({
      query: (body) => ({
        url: "/services",
        method: "POST",
        body,
      }),
      invalidatesTags: ["services"],
      onQueryStarted(body, { queryFulfilled }) {
        queryFulfilled
          .then(() => {
            notification({
              type: "success",
              title: "Success!",
              description: "A new service was created successfully",
            });
          })
          .catch(() => {
            notification({
              type: "error",
              title: "Unable to update service!",
              description: "Please try again later after sometime",
            });
          });
      },
    }),
    updateService: builder.mutation({
      query: ({ serviceId, body }) => ({
        url: `/services/${serviceId}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: ["services"],
      onQueryStarted({ serviceId, body }, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          swasnaAdminApi.util.updateQueryData(
            "getServices",
            undefined,
            (draft) => {
              const i = draft.findIndex((el) => el._id === serviceId);
              draft[i] = { ...draft[i], ...body };
            },
          ),
        );
        queryFulfilled
          .then(() => {
            notification({
              type: "success",
              title: "Success!",
              description: "Service updated successfully",
            });
          })
          .catch(() => {
            notification({
              type: "error",
              title: "Unable to update service!",
              description: "Please try again later after sometime",
            });
            patchResult.undo();
          });
      },
    }),
    deleteService: builder.mutation({
      query: ({ serviceId }) => ({
        url: `/services/${serviceId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["services"],
      onQueryStarted({ serviceId }, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          swasnaAdminApi.util.updateQueryData(
            "getServices",
            undefined,
            (draft) => {
              const i = draft.findIndex((el) => el._id === serviceId);
              draft.splice(i, 1);
            },
          ),
        );
        queryFulfilled
          .then(() => {
            notification({
              type: "success",
              title: "Success!",
              description: "Service deleted successfully",
            });
          })
          .catch(() => {
            notification({
              type: "error",
              title: "Unable to delete service!",
              description: "Please try again later after sometime",
            });
            patchResult.undo();
          });
      },
    }),
    getPackages: builder.query({
      query: () => ({
        url: "/packages",
        method: "GET",
      }),
      providesTags: ["packages"],
    }),
    createPackage: builder.mutation({
      query: (body) => ({
        url: "/packages",
        method: "POST",
        body,
      }),
      invalidatesTags: ["packages"],
      onQueryStarted(body, { queryFulfilled }) {
        queryFulfilled
          .then(() => {
            notification({
              type: "success",
              title: "Success!",
              description: "Package created successfully",
            });
          })
          .catch(() => {
            notification({
              type: "error",
              title: "Unable to add package!",
              description: "Please try again later after sometime",
            });
          });
      },
    }),
    updatePackage: builder.mutation({
      query: ({ packageId, body }) => ({
        url: `/packages/${packageId}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: ["packages"],
      onQueryStarted({ packageId, body }, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          swasnaAdminApi.util.updateQueryData(
            "getPackages",
            undefined,
            (draft) => {
              const i = draft.findIndex((el) => el._id === packageId);
              draft[i] = { ...draft[i], ...body };
            },
          ),
        );
        queryFulfilled
          .then(() => {
            notification({
              type: "success",
              title: "Success!",
              description: "Package updated successfully",
            });
          })
          .catch(() => {
            notification({
              type: "error",
              title: "Unable to update package!",
              description: "Please try again later after sometime",
            });
            patchResult.undo();
          });
      },
    }),
    deletePackage: builder.mutation({
      query: ({ packageId }) => ({
        url: `/packages/${packageId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["packages"],
      onQueryStarted({ packageId }, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          swasnaAdminApi.util.updateQueryData(
            "getPackages",
            undefined,
            (draft) => {
              const i = draft.findIndex((el) => el._id === packageId);
              draft.splice(i, 1);
            },
          ),
        );
        queryFulfilled
          .then(() => {
            notification({
              type: "success",
              title: "Success!",
              description: "Package deleted successfully",
            });
          })
          .catch(() => {
            notification({
              type: "error",
              title: "Unable to delete service!",
              description: "Please try again later after sometime",
            });
            patchResult.undo();
          });
      },
    }),
    recommendMembership: builder.mutation({
      query: (body) => ({
        url: "/memberships/recommend",
        method: "POST",
        body,
      }),
    }),
    joinMembership: builder.mutation({
      query: (body) => ({
        url: "/memberships/join",
        method: "POST",
        body,
      }),
    }),
    getProviders: builder.query({
      query: ({ providerId = "", ...queryParams }) => ({
        url: `/providers/${providerId && `${providerId}`}?${serializeQueryParams(queryParams)}`,
        method: "GET",
      }),
      providesTags: ["providers"],
    }),
    createProvider: builder.mutation({
      query: (body) => ({
        url: "/providers",
        method: "POST",
        body,
      }),
      invalidatesTags: ["providers", "analytics"],
      onQueryStarted(body, { queryFulfilled }) {
        queryFulfilled
          .then(() => {
            notification({
              type: "success",
              title: "Success!",
              description: "Provider created successfully",
            });
          })
          .catch((err) => {
            notification({
              type: "error",
              title: "Unable to add provider!",
              description:
                err?.error?.data?.message ||
                err?.data?.message ||
                "Please try again later after sometime",
            });
          });
      },
    }),
    updateProvider: builder.mutation({
      query: ({ providerId, body }) => ({
        url: `/providers/${providerId}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: ["providers", "analytics"],
      onQueryStarted({ providerId, body }, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          swasnaAdminApi.util.updateQueryData(
            "getProviders",
            undefined,
            (draft) => {
              const i = draft.findIndex((el) => el._id === providerId);
              draft[i] = { ...draft[i], ...body };
            },
          ),
        );
        queryFulfilled
          .then(() => {
            notification({
              type: "success",
              title: "Success!",
              description: "Provider updated successfully",
            });
          })
          .catch(() => {
            notification({
              type: "error",
              title: "Unable to update provider!",
              description: "Please try again later after sometime",
            });
            patchResult.undo();
          });
      },
    }),
    deleteProvider: builder.mutation({
      query: ({ providerId }) => ({
        url: `/providers/${providerId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["providers", "analytics"],
      onQueryStarted({ providerId }, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          swasnaAdminApi.util.updateQueryData(
            "getProviders",
            undefined,
            (draft) => {
              const i = draft.findIndex((el) => el._id === providerId);
              draft.splice(i, 1);
            },
          ),
        );
        queryFulfilled
          .then(() => {
            notification({
              type: "success",
              title: "Success!",
              description: "Provider deleted successfully",
            });
          })
          .catch(() => {
            notification({
              type: "error",
              title: "Unable to delete provider!",
              description: "Please try again later after sometime",
            });
            patchResult.undo();
          });
      },
    }),
    getExpertise: builder.query({
      query: ({ ...queryParams }) => ({
        url: `/expertise?${serializeQueryParams(queryParams)}`,
        method: "GET",
      }),
      providesTags: ["expertise"],
    }),
    createExpertise: builder.mutation({
      query: (body) => ({
        url: "/expertise",
        method: "POST",
        body,
      }),
      invalidatesTags: ["expertise"],
      onQueryStarted(body, { queryFulfilled }) {
        queryFulfilled
          .then(() => {
            notification({
              type: "success",
              title: "Success!",
              description: "Expertise created successfully",
            });
          })
          .catch(() => {
            notification({
              type: "error",
              title: "Unable to add expertise!",
              description: "Please try again later after sometime",
            });
          });
      },
    }),
    updateExpertise: builder.mutation({
      query: ({ expertiseId, body }) => ({
        url: `/expertise/${expertiseId}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: ["expertise"],
      onQueryStarted({ expertiseId, body }, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          swasnaAdminApi.util.updateQueryData(
            "getExpertise",
            undefined,
            (draft) => {
              const i = draft.findIndex((el) => el._id === expertiseId);
              draft[i] = { ...draft[i], ...body };
            },
          ),
        );
        queryFulfilled
          .then(() => {
            notification({
              type: "success",
              title: "Success!",
              description: "Expertise updated successfully",
            });
          })
          .catch(() => {
            notification({
              type: "error",
              title: "Unable to update expertise!",
              description: "Please try again later after sometime",
            });
            patchResult.undo();
          });
      },
    }),
    deleteExpertise: builder.mutation({
      query: ({ expertiseId }) => ({
        url: `/expertise/${expertiseId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["expertise"],
      onQueryStarted({ expertiseId }, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          swasnaAdminApi.util.updateQueryData(
            "getExpertise",
            undefined,
            (draft) => {
              const i = draft.findIndex((el) => el._id === expertiseId);
              draft.splice(i, 1);
            },
          ),
        );
        queryFulfilled
          .then(() => {
            notification({
              type: "success",
              title: "Success!",
              description: "Expertise deleted successfully",
            });
          })
          .catch(() => {
            notification({
              type: "error",
              title: "Unable to delete expertise!",
              description: "Please try again later after sometime",
            });
            patchResult.undo();
          });
      },
    }),
    getMemberships: builder.query({
      query: ({ membershipId = "", ...queryParams }) => ({
        url: `/memberships${membershipId && `/${membershipId}`}?${serializeQueryParams(queryParams)}`,
        method: "GET",
      }),
      providesTags: ["memberships"],
    }),
    updateMemberships: builder.mutation({
      query: ({ membershipId, body }) => ({
        url: `/memberships/${membershipId}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: ["memberships"],
      onQueryStarted({ membershipId, body }, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          swasnaAdminApi.util.updateQueryData(
            "getMemberships",
            undefined,
            (draft) => {
              const i = draft.findIndex((el) => el._id === membershipId);
              draft[i] = { ...draft[i], ...body };
            },
          ),
        );
        queryFulfilled
          .then(() => {
            notification({
              type: "success",
              title: "Success!",
              description: "Membership updated successfully",
            });
          })
          .catch(() => {
            notification({
              type: "error",
              title: "Unable to update membership!",
              description: "Please try again later after sometime",
            });
            patchResult.undo();
          });
      },
    }),
    changeMembership: builder.mutation({
      query: ({ membershipId, body }) => ({
        url: `/change-membership/${membershipId}`,
        method: "POST",
        body,
      }),
      // invalidatesTags: ['memberships'],
      onQueryStarted({ membershipId, body }, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          swasnaAdminApi.util.updateQueryData(
            "getMemberships",
            undefined,
            (draft) => {
              const i = draft.findIndex((el) => el._id === membershipId);
              draft[i] = { ...draft[i], ...body };
            },
          ),
        );
        queryFulfilled
          .then(() => {
            notification({
              type: "success",
              title: "Success!",
              description: "Membership changed successfully",
            });
          })
          .catch(() => {
            notification({
              type: "error",
              title: "Unable to change membership!",
              description: "Please try again later after sometime",
            });
            patchResult.undo();
          });
      },
    }),

    // refund membership
    refundMembership: builder.mutation({
      query: ({ body }) => ({
        url: "/memberships/refund",
        method: "POST",
        body,
      }),
      invalidatesTags: ["memberships"],
      onQueryStarted({ membershipId, body }, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          swasnaAdminApi.util.updateQueryData(
            "getMemberships",
            undefined,
            (draft) => {
              const i = draft.findIndex((el) => el._id === membershipId);
              draft[i] = { ...draft[i], ...body };
            },
          ),
        );
        queryFulfilled
          .then(() => {
            notification({
              type: "success",
              title: "Success!",
              description: "Refund initiated successfully",
            });
          })
          .catch((err) => {
            notification({
              type: "error",
              title: "Unable to issue refund!",
              description:
                err?.error?.data?.message ||
                "Please try again later after sometime",
            });
            patchResult.undo();
          });
      },
    }),

    getClients: builder.query({
      query: ({ client = "", ...queryParams }) => ({
        url: `/clients${client.length > 2 ? `/${client}` : ""}?${serializeQueryParams(queryParams)}`,
        method: "GET",
      }),
      providesTags: ["client"],
    }),

    // Family Member APIs
    getFamilyData: builder.query({
      query: ({ client = "" }) => ({
        url: `/clients/familyHistory/${client}`,
        method: "GET",
      }),
      providesTags: ["family"],
    }),

    // Clinical Data apis
    getClinicalData: builder.query({
      query: ({ client = "" }) => ({
        url: `/clinicalData/${client}`,
        method: "GET",
      }),
      providesTags: ["clinical"],
    }),

    addClinicalData: builder.mutation({
      query: ({ client, ...body }) => ({
        url: `/clinicalData/${client}`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["clinical"],
    }),

    updateClinicalData: builder.mutation({
      query: ({ client, ...body }) => ({
        url: `/clinicalData/${client}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: ["clinical"],
    }),

    updateClient: builder.mutation({
      query: ({ clientId = "", ...body }) => ({
        url: `/clients/${clientId}`,
        method: "PATCH",
        body,
      }),
      onQueryStarted(body, { queryFulfilled }) {
        queryFulfilled
          .then(() => {
            notification({
              type: "success",
              title: "Success!",
              description: "Client updated successfully",
            });
          })
          .catch((error) => {
            if (error.data.code === "BAD_INPUT_ERROR") {
              notification({
                title: "OOPs!",
                description: error.data.message,
              });
            } else {
              notification({
                title: "Something went wrong!",
                description: "Please try again",
              });
            }
          });
      },
      invalidatesTags: ["client"],
    }),
    getAvailability: builder.query({
      query: (body = {}) => ({
        url: `/availability/${body?.availabilityId || ""}`,
        method: "GET",
      }),
      providesTags: ["availability"],
    }),
    checkAvailability: builder.query({
      query: ({ providerId = "", selectedDate, duration }) => ({
        method: "GET",
        url: `/providers/${providerId}/availability?selectedDate=${selectedDate}&interval=${duration}&timeZone=${timezone}`,
      }),
      providesTags: ["availability"],
    }),
    getCalendarEvents: builder.query({
      query: ({ providerId = "", selectedDate }) => ({
        method: "GET",
        url: `/provider/calendar/events/${providerId}?selectedDate=${selectedDate}`,
      }),
    }),
    addAvailability: builder.mutation({
      query: (body = {}) => ({
        url: "/availability",
        method: "POST",
        body,
      }),
      invalidatesTags: ["availability", "providers"],
    }),
    updateAvailability: builder.mutation({
      query: ({ availabilityId = "", ...body }) => ({
        url: `/availability/${availabilityId}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: ["availability", "providers"],
    }),

    // unavailability
    addUnavailability: builder.mutation({
      query: ({ ...body }) => ({
        url: "/unAvailability",
        method: "POST",
        body,
      }),
      invalidatesTags: ["availability", "providers"],
    }),
    getAppointments: builder.query({
      query: ({ appointmentId, ...queryParams }) => ({
        url: `/appointment?${serializeQueryParams(queryParams)}`,
        method: "GET",
      }),
      providesTags: ["appointments"],
    }),

    // create appointment
    addAppointment: builder.mutation({
      query: (body) => ({
        url: "/appointment",
        method: "POST",
        body,
      }),
      invalidatesTags: ["appointments", "memberships"],
    }),

    getAccessToken: builder.query({
      query: ({ appointmentId = "" }) => ({
        url: `/appointment/getAccessToken/${appointmentId}`,
        method: "GET",
      }),
      providesTags: ["appointments"],
    }),
    updateAppointment: builder.mutation({
      query: ({ appointmentId = "", ...body }) => ({
        url: `/appointment/${appointmentId}`,
        method: "PATCH",
        body,
      }),
      onQueryStarted(body, { queryFulfilled }) {
        queryFulfilled.catch((error) => {
          if (error.data.code === "BAD_INPUT_ERROR") {
            notification({
              title: "OOPs!",
              description: error.data.message,
            });
          } else {
            notification({
              title: "Something went wrong!",
              description: "Please try again",
            });
          }
        });
      },
      invalidatesTags: ["appointments", "analytics"],
    }),
    sendPaymentLink: builder.mutation({
      query: ({ appointmentId = "" }) => ({
        url: `/appointment/sendPaymentLink/${appointmentId}`,
        method: "POST",
      }),
    }),
    uploadFile: builder.mutation({
      query: (body) => ({
        url: "/common/uploadFile",
        method: "POST",
        body,
      }),
    }),
    addMealPlan: builder.mutation({
      query: ({ appointmentId = "", ...body }) => ({
        url: `/appointment/addMealPlan/${appointmentId}`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["appointments"],
    }),

    // Lead APIs
    getLeads: builder.query({
      query: ({ leadId = "", ...queryParams }) => ({
        url: `/leads/${leadId}?${serializeQueryParams(queryParams)}`,
        method: "GET",
      }),
      providesTags: ["leads"],
    }),

    updateLeads: builder.mutation({
      query: ({ leadId = "", ...body }) => ({
        url: `/leads/${leadId}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: ["leads"],
    }),

    deleteLead: builder.mutation({
      query: ({ leadId }) => ({
        url: `/leads/${leadId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["leads", "analytics"],
      onQueryStarted({ leadId }, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          swasnaAdminApi.util.updateQueryData(
            "getLeads",
            undefined,
            (draft) => {
              const i = draft.findIndex((el) => el._id === leadId);
              draft.splice(i, 1);
            },
          ),
        );
        queryFulfilled
          .then(() => {
            notification({
              type: "success",
              title: "Success!",
              description: "Lead deleted successfully",
            });
          })
          .catch((err) => {
            notification({
              type: "error",
              title: "Unable to delete Lead!",
              description:
                err?.error?.data?.message ||
                "Please try again later after sometime.",
            });
            patchResult.undo();
          });
      },
    }),

    // create lead
    addLeadAppointment: builder.mutation({
      query: (body) => ({
        url: "/appointment/lead",
        method: "POST",
        body,
      }),
      invalidatesTags: ["leads"],
    }),

    // Notes APIs
    getNotes: builder.query({
      query: ({ ...queryParams }) => ({
        url: `/notes?${serializeQueryParams(queryParams)}`,
        method: "GET",
      }),
      providesTags: ["notes"],
    }),

    // create Notes
    addNotes: builder.mutation({
      query: ({ body }) => ({
        url: "/notes",
        method: "POST",
        body,
      }),
      invalidatesTags: ["notes"],
    }),

    // Approve meal plans
    approvePlan: builder.mutation({
      query: ({ appointmentId = "", ...body }) => ({
        url: `/appointment/approveMealPlan/${appointmentId}`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["appointments"],
    }),

    // get insurance Receipt
    getInsuranceReceipt: builder.query({
      query: ({ appointmentId }) => ({
        url: `/appointment/insurance/${appointmentId}`,
        method: "GET",
      }),
    }),

    // Get Analytics (Dashboard) data
    getAnalytics: builder.query({
      query: ({ ...queryParams }) => ({
        url: `/analytics/getAnalytics?${serializeQueryParams(queryParams)}`,
        method: "GET",
      }),
      providesTags: ["analytics"],
    }),

    // profile apis
    getProfile: builder.query({
      query: () => ({
        url: "/profile",
        method: "GET",
      }),
      providesTags: ["profile"],
    }),
    updateProfile: builder.mutation({
      query: ({ ...body }) => ({
        url: "/profile",
        method: "PATCH",
        body,
      }),
      invalidatesTags: ["profile"],
    }),
    changePassword: builder.mutation({
      query: ({ ...body }) => ({
        url: "/changePassword",
        method: "PATCH",
        body,
      }),
      invalidatesTags: ["profile"],
    }),

    // settings api

    getSettings: builder.query({
      query: () => ({
        url: "/settings",
        method: "GET",
      }),
      providesTags: ["settings"],
    }),

    updateSettings: builder.mutation({
      query: ({ ...body }) => ({
        url: "/settings",
        method: "PATCH",
        body,
      }),
      invalidatesTags: ["settings"],
    }),

    // Notifications APIs

    // get notifications data
    getNotifications: builder.query({
      query: ({ id = "" }) => ({
        url: `/notifications/${id}`,
        method: "GET",
      }),
      providesTags: ["notifications"],
    }),

    // update notifications data
    updateNotifications: builder.mutation({
      query: ({ body, id }) => ({
        url: `/notifications/${id}`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["notifications"],
    }),

    // get remainders data
    getRemainders: builder.query({
      query: ({ ...queryParams }) => ({
        url: `/reminder?${serializeQueryParams(queryParams)}`,
        method: "GET",
      }),
      providesTags: ["remainders"],
    }),

    addRemainder: builder.mutation({
      query: (body = {}) => ({
        url: "/reminder",
        method: "POST",
        body,
      }),
      invalidatesTags: ["remainders"],
    }),

    // update notifications data
    updateRemainders: builder.mutation({
      query: ({ body, id }) => ({
        url: `/reminder/${id}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: ["remainders"],
      onQueryStarted({ id, body }, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          swasnaAdminApi.util.updateQueryData(
            "getRemainders",
            undefined,
            (draft) => {
              const i = draft.findIndex((el) => el._id === id);
              draft[i] = { ...draft[i], ...body };
            },
          ),
        );
        queryFulfilled
          .then(() => {
            notification({
              type: "success",
              title: "Success!",
              description: "Reminder updated successfully",
            });
          })
          .catch(() => {
            notification({
              type: "error",
              title: "Unable to update reminder!",
              description: "Please try again later after sometime",
            });
            patchResult.undo();
          });
      },
    }),

    // delete remainder
    deleteRemainder: builder.mutation({
      query: ({ remainderId }) => ({
        url: `/reminder/${remainderId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["remainders"],
      onQueryStarted({ id }, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          swasnaAdminApi.util.updateQueryData(
            "getRemainders",
            undefined,
            (draft) => {
              const i = draft.findIndex((el) => el._id === id);
              draft.splice(i, 1);
            },
          ),
        );
        queryFulfilled
          .then(() => {
            notification({
              type: "success",
              title: "Success!",
              description: "Reminder deleted successfully",
            });
          })
          .catch(() => {
            notification({
              type: "error",
              title: "Unable to delete reminder!",
              description: "Please try again later after sometime",
            });
            patchResult.undo();
          });
      },
    }),

    // updated events and remainders api
    getEventTags: builder.query({
      query: () => ({
        url: "/Reminder/tags",
        method: "GET",
      }),
      providesTags: ["remainders"],
    }),

    // gete event list
    getEventList: builder.query({
      query: () => ({
        url: "/Reminder/titles",
        method: "GET",
      }),
      providesTags: ["remainders"],
    }),

    // Send token for logged in google user
    addGoogleToken: builder.mutation({
      query: ({ body, providerId }) => ({
        url: `/providers/calendar/${providerId}`,
        method: "POST",
        body,
      }),
    }),

    // transactions
    getTransactions: builder.query({
      query: ({ ...queryParams }) => ({
        url: `/transactions?${serializeQueryParams(queryParams)}`,
        method: "GET",
      }),
      providesTags: ["transactions"],
    }),

    // Upload csv files
    importClient: builder.mutation({
      query: ({ body }) => ({
        url: "/import/client",
        method: "POST",
        body,
      }),
    }),

    importMemberships: builder.mutation({
      query: ({ body }) => ({
        url: "/import/membership",
        method: "POST",
        body,
      }),
    }),

    // client notes
    addNote: builder.mutation({
      query: ({ clientId, body = {} }) => ({
        url: `/client/${clientId}/addNote`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["client"],
    }),

    // delete remainder
    deleteNote: builder.mutation({
      query: ({ clientId, noteId }) => ({
        url: `/client/${clientId}/deleteNote/${noteId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["client"],
      onQueryStarted({ clientId }, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          swasnaAdminApi.util.updateQueryData(
            "getClients",
            undefined,
            (draft) => {
              const i = draft.findIndex((el) => el._id === clientId);
              draft.splice(i, 1);
            },
          ),
        );
        queryFulfilled
          .then(() => {
            notification({
              type: "success",
              title: "Success!",
              description: "Note deleted successfully",
            });
          })
          .catch(() => {
            notification({
              type: "error",
              title: "Unable to delete note!",
              description: "Please try again later after sometime",
            });
            patchResult.undo();
          });
      },
    }),
  }),
});

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
  useLoginMutation,
  useVerifyMutation,
  useForgotPasswordMutation,
  useResetPasswordMutation,
  useGetServicesQuery,
  useCreateServiceMutation,
  useUpdateServiceMutation,
  useDeleteServiceMutation,
  useGetPackagesQuery,
  useCreatePackageMutation,
  useUpdatePackageMutation,
  useDeletePackageMutation,
  useRecommendMembershipMutation,
  useJoinMembershipMutation,
  useGetProvidersQuery,
  useLazyGetProvidersQuery,
  useCreateProviderMutation,
  useUpdateProviderMutation,
  useDeleteProviderMutation,
  useGetExpertiseQuery,
  useCreateExpertiseMutation,
  useDeleteExpertiseMutation,
  useUpdateExpertiseMutation,
  useGetMembershipsQuery,
  useUpdateMembershipsMutation,
  useRefundMembershipMutation,
  useGetClientsQuery,
  useLazyGetClientsQuery,
  useUpdateClientMutation,
  useLazyGetAvailabilityQuery,
  useAddAvailabilityMutation,
  useUpdateAvailabilityMutation,
  useAddUnavailabilityMutation,
  useGetAppointmentsQuery,
  useLazyGetAppointmentsQuery,
  useUpdateAppointmentMutation,
  useLazyCheckAvailabilityQuery,
  useLazyGetCalendarEventsQuery,
  useSendPaymentLinkMutation,
  useGetAccessTokenQuery,
  useUploadFileMutation,
  useAddMealPlanMutation,
  useGetLeadsQuery,
  useDeleteLeadMutation,
  useAddLeadAppointmentMutation,
  useGetNotesQuery,
  useAddNotesMutation,
  useApprovePlanMutation,
  useUpdateLeadsMutation,
  useLazyGetInsuranceReceiptQuery,
  useGetAnalyticsQuery,
  useChangePasswordMutation,
  useGetProfileQuery,
  useUpdateProfileMutation,
  useGetSettingsQuery,
  useUpdateSettingsMutation,
  useGetNotificationsQuery,
  useLazyGetNotificationsQuery,
  useUpdateNotificationsMutation,
  useGetRemaindersQuery,
  useLazyGetRemaindersQuery,
  useAddRemainderMutation,
  useUpdateRemaindersMutation,
  useGetClinicalDataQuery,
  useUpdateClinicalDataMutation,
  useAddClinicalDataMutation,
  useGetFamilyDataQuery,
  useAddAppointmentMutation,
  useAddGoogleTokenMutation,
  useGetTransactionsQuery,
  useLazyGetTransactionsQuery,
  useChangeMembershipMutation,
  useGetEventTagsQuery,
  useGetEventListQuery,
  useDeleteRemainderMutation,
  useImportClientMutation,
  useImportMembershipsMutation,
  useAddNoteMutation,
  useDeleteNoteMutation,
} = swasnaAdminApi;
